import {
  archiveBlockedReasonLabel,
  canArchiveNotificationRow,
  canReplyInThread,
  isThreadOutboundOnlyForViewer,
  normalizeContactVisibilityLevel,
} from "@/lib/messages/workplaceMessagesPolicy";
import type { MemberRow, NotifRow, ParticipantReceipt, ViewerInfo } from "./workplaceMessagesTypes";

export type ThreadDetailProps = {
  p: {
    messages: NotifRow[];
    participantReceipts: ParticipantReceipt[];
    members: MemberRow[];
    viewer: ViewerInfo | null;
    selectedThreadId: string | null;
    replyText: string;
    setReplyText: (v: string) => void;
    replyBusy: boolean;
    markBusy: boolean;
    archiveBusy: boolean;
    allowRequiresAck: boolean;
    replyRequiresAck: boolean;
    setReplyRequiresAck: (v: boolean) => void;
    onSendReply: () => void | Promise<void>;
    onMarkOne: (id: string) => void | Promise<void>;
    onAcknowledge: (id: string) => void | Promise<void>;
    onArchiveOne: (id: string) => void | Promise<void>;
    onArchiveThread: (threadId: string) => void | Promise<void>;
  };
};

export function WorkplaceThreadDetail({ p }: ThreadDetailProps) {
  const replyPolicy = computeThreadReplyPolicy(p);
  return (
    <div className="rounded-xl border border-gray-200 p-2 dark:border-zinc-700">
      <ul className="max-h-40 space-y-2 overflow-y-auto">
        {p.messages.map((n) => (
          <li
            key={n.id}
            className={
              "rounded-lg border border-transparent px-2 py-1.5 text-xs"
            }
          >
            <div className="font-medium">{n.title}</div>
            <div className="text-[11px] text-gray-600 dark:text-gray-400">{n.body}</div>
            {n.requires_ack ? (
              <div className="mt-0.5 text-[10px] text-amber-700 dark:text-amber-300">
                Requires acknowledgement
                {n.ack_deadline_at ? ` · due ${formatAckDeadline(n.ack_deadline_at)}` : ""}
                {n.acknowledged_at ? " · Acknowledged" : ""}
              </div>
            ) : null}
            <ThreadMessageActions p={p} n={n} />
          </li>
        ))}
      </ul>
      <ParticipantReceiptsSummary p={p} />
      {replyPolicy.canReply ? (
        <div className="mt-2 space-y-2">
          <div className="flex gap-2">
            <input
              value={p.replyText}
              onChange={(e) => p.setReplyText(e.target.value)}
              placeholder="Reply to thread…"
              className="flex-1 rounded-md border border-gray-200 px-2 py-1 text-xs dark:border-zinc-700 dark:bg-zinc-800"
            />
            <button
              type="button"
              disabled={p.replyBusy || !p.replyText.trim()}
              onClick={() => void p.onSendReply()}
              className="rounded-md bg-blue-600 px-2 py-1 text-xs text-white disabled:opacity-50"
            >
              Send
            </button>
          </div>
          {p.allowRequiresAck ? (
            <label className="flex items-center gap-2 text-[11px] text-gray-600 dark:text-gray-300">
              <input
                type="checkbox"
                checked={p.replyRequiresAck}
                onChange={(e) => p.setReplyRequiresAck(e.target.checked)}
                className="h-3 w-3"
              />
              Require acknowledgement
            </label>
          ) : null}
        </div>
      ) : (
        <p className="mt-2 text-[10px] text-gray-500 dark:text-gray-400">{replyPolicy.hint}</p>
      )}
      <ThreadArchiveButton p={p} />
    </div>
  );
}

function formatAckDeadline(raw: string): string {
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? raw : d.toLocaleString();
}

function ParticipantReceiptsSummary({ p }: ThreadDetailProps) {
  const receipts = p.participantReceipts.filter((r) => r.user_id);
  if (receipts.length === 0) {
    return (
      <div className="mt-2 rounded-md border border-gray-100 px-2 py-1 text-[10px] text-gray-600 dark:border-zinc-700 dark:text-gray-300">
        Read receipts: mark your copy read to update your status.
      </div>
    );
  }
  return (
    <div className="mt-2 rounded-md border border-gray-100 px-2 py-1 text-[10px] text-gray-600 dark:border-zinc-700 dark:text-gray-300 space-y-0.5">
      <div className="font-medium text-gray-700 dark:text-gray-200">Read receipts</div>
      {receipts.map((r) => {
        const member = p.members.find((m) => m.user_id === r.user_id);
        const label = member?.name || r.user_id.slice(0, 8);
        const status = r.read_at ? "Read" : "Unread";
        return (
          <div key={r.user_id}>
            {label} (L{member ? member.trust_level : "?"}) — {status}
          </div>
        );
      })}
    </div>
  );
}

function computeThreadReplyPolicy(p: ThreadDetailProps["p"]): { canReply: boolean; hint: string } {
  const viewer = p.viewer;
  if (!viewer) {
    return { canReply: false, hint: "Sign in to reply in this thread." };
  }
  const viewerLevel = normalizeContactVisibilityLevel(viewer.trust_level);
  const trustFor = (userId: string) => {
    if (userId === viewer.user_id) return viewerLevel;
    const row = p.members.find((m) => m.user_id === userId);
    return normalizeContactVisibilityLevel(row?.trust_level);
  };
  const participantIds = [
    ...new Set([
      ...p.participantReceipts.map((r) => r.user_id),
      ...p.messages.map((m) => m.sender_user_id).filter((id): id is string => Boolean(id)),
      viewer.user_id,
    ]),
  ];
  if (
    isThreadOutboundOnlyForViewer(viewer.user_id, viewerLevel, p.messages, trustFor) &&
    !canReplyInThread(viewer.user_id, viewerLevel, participantIds, trustFor)
  ) {
    return {
      canReply: false,
      hint: "Confirm-only thread — you can mark read or acknowledge, but cannot reply up to management.",
    };
  }
  if (!canReplyInThread(viewer.user_id, viewerLevel, participantIds, trustFor)) {
    return { canReply: false, hint: "You cannot reply in this thread at your contact level." };
  }
  return { canReply: true, hint: "" };
}

function ThreadMessageActions({ p, n }: { p: ThreadDetailProps["p"]; n: NotifRow }) {
  const archiveDecision = computeArchiveDecision(p, n);
  return (
    <div className="mt-1 flex flex-wrap items-center gap-3">
      {!n.read_at ? (
        <button
          type="button"
          disabled={p.markBusy}
          className="text-[10px] text-blue-700 dark:text-blue-300"
          onClick={() => void p.onMarkOne(n.id)}
        >
          Mark read
        </button>
      ) : null}
      {n.requires_ack && !n.acknowledged_at ? (
        <button
          type="button"
          disabled={p.markBusy}
          className="text-[10px] text-amber-700 dark:text-amber-300"
          onClick={() => void p.onAcknowledge(n.id)}
        >
          Acknowledge
        </button>
      ) : null}
      <button
        type="button"
        disabled={p.archiveBusy || !archiveDecision.allowed}
        title={archiveDecision.allowed ? "Archive this message" : archiveBlockedReasonLabel(archiveDecision.reason)}
        aria-label="Archive message"
        onClick={() => void p.onArchiveOne(n.id)}
        className="ml-auto text-[10px] text-gray-500 hover:text-red-700 disabled:opacity-40 dark:text-gray-400 dark:hover:text-red-300"
      >
        ✕
      </button>
    </div>
  );
}

function ThreadArchiveButton({ p }: ThreadDetailProps) {
  if (!p.selectedThreadId || p.messages.length === 0) return null;
  const archivable = p.messages.filter((n) => computeArchiveDecision(p, n).allowed).length;
  const blocked = p.messages.length - archivable;
  const firstBlocked = p.messages.find((n) => !computeArchiveDecision(p, n).allowed);
  const firstBlockedDecision = firstBlocked ? computeArchiveDecision(p, firstBlocked) : null;
  const blockedHint =
    firstBlockedDecision && !firstBlockedDecision.allowed
      ? archiveBlockedReasonLabel(firstBlockedDecision.reason)
      : "All messages are gated (ack / sender-lock / authority).";
  const label =
    archivable === p.messages.length
      ? "Archive thread"
      : `Archive thread (${archivable} of ${p.messages.length} eligible)`;
  return (
    <div className="mt-2 flex items-center justify-end">
      <button
        type="button"
        disabled={p.archiveBusy || archivable === 0}
        onClick={() => void p.onArchiveThread(p.selectedThreadId!)}
        title={archivable === 0 ? blockedHint : `${blocked ? `${blocked} gated.` : ""}`}
        className="rounded-md border border-gray-200 px-2 py-1 text-[11px] text-gray-700 hover:bg-gray-50 disabled:opacity-40 dark:border-zinc-700 dark:text-gray-200 dark:hover:bg-zinc-800"
      >
        {label}
      </button>
    </div>
  );
}

function computeArchiveDecision(p: ThreadDetailProps["p"], n: NotifRow) {
  const viewer = p.viewer;
  if (!viewer) {
    return { allowed: false, reason: "not-yours" as const };
  }
  const seedId = n.parent_notification_id;
  const senderHasArchivedTheirSeed = !seedId || !p.messages.some((m) => m.id === seedId);
  const senderRow = p.members.find((m) => m.user_id === n.sender_user_id) ?? null;
  const senderLevel = senderRow ? normalizeContactVisibilityLevel(senderRow.trust_level) : null;
  return canArchiveNotificationRow({
    viewerUserId: viewer.user_id,
    viewerTrustLevel: normalizeContactVisibilityLevel(viewer.trust_level),
    row: {
      user_id: viewer.user_id,
      archived_at: n.archived_at ?? null,
      requires_ack: n.requires_ack === true,
      acknowledged_at: n.acknowledged_at ?? null,
      sender_locked: n.sender_locked === true,
      sender_user_id: n.sender_user_id ?? null,
      parent_notification_id: n.parent_notification_id ?? null,
    },
    senderTrustLevel: senderLevel,
    senderHasArchivedTheirSeed,
  });
}
